import { SECTION_IDS, type MarketingFrame, type MarketingLayout, type MarketingViewport, type SectionId } from "../types";
import type { MarketingFramePort } from "../experience";
import { SECTION_SCORE, validateScore } from "../score";

import { PINNED_SECTIONS, sampleBlockPin } from "./pin";

export * from "./pin";

const smooth = (value: number): number => {
  const t = Math.max(0, Math.min(1, value));
  return t * t * (3 - 2 * t);
};

/**
 * Держит блоки в кадре и отдаёт им ход их собственной анимации.
 * Ничего не рисует сам: только встречное смещение и прогресс в переменных,
 * которыми пользуются раскладка и оформление блока.
 *
 * Затухания блока здесь нет и не должно быть. Блок приезжает и уезжает
 * прокруткой — как на обычном сайте. Гашение непрозрачностью превращало
 * маршрут в слайд-шоу и съедало края каждой анимации.
 */
export class MarketingBlocks implements MarketingFramePort {
  private readonly sections: ReadonlyMap<SectionId, HTMLElement>;
  private layout: MarketingLayout | null = null;
  private readonly route: Element | null;

  constructor(root: ParentNode) {
    const found = new Map<SectionId, HTMLElement>();
    for (const id of SECTION_IDS) {
      const node = root.querySelector<HTMLElement>(`[data-section-id="${id}"]`);
      if (node) found.set(id, node);
    }
    this.sections = found;
    const route = root instanceof Element ? root : null;
    if (route) route.setAttribute("data-blocks-mounted", "true");
    this.route = route;
    /* Партитура проверяет сама себя при монтировании: если такту досталось
       меньше экрана, чем нужно, чтобы его прочитали, это видно сразу в консоли,
       а не через месяц на живом сайте. */
    for (const warning of validateScore()) {
      console.warn(`[партитура] ${warning.id}: ${warning.message}`);
    }
  }

  render(frame: MarketingFrame): { state: "managed" } {
    const layout = this.layout;
    if (!layout) return { state: "managed" };
    for (const [id, node] of this.sections) {
      const score = SECTION_SCORE[id];
      const pin = sampleBlockPin(frame, layout, id);
      // Pinning cancels the route's physical translation. Reduced motion keeps
      // the same section spans, so removing this geometry leaves empty holds.
      if (score.pin) node.style.setProperty("--block-pin-y", `${pin.offsetPx.toFixed(2)}px`);
      else node.style.removeProperty("--block-pin-y");
      if (frame.motion === "reduced") {
        node.style.setProperty("--block-progress", "1");
        node.style.setProperty("--block-progress-in", "1");
        node.style.setProperty("--block-progress-late", "1");
        node.style.setProperty("--block-presence", "1");
        node.dataset.blockHeld = pin.onStage ? "true" : "false";
        node.dataset.blockPhase = pin.phase;
        continue;
      }

      node.style.setProperty("--block-progress", pin.progress.toFixed(4));
      /* Первый и второй эшелон входа. Оба идут внутри окна анимации, а значит
         внутри удержания: пороги 0.25 и 0.22..0.56 больше не могут попасть
         на въезд или на выезд, как это было раньше. */
      node.style.setProperty("--block-progress-in", smooth(pin.progress / 0.25).toFixed(4));
      node.style.setProperty("--block-progress-late", smooth((pin.progress - 0.22) / 0.34).toFixed(4));
      /* Переменная сохранена ради совместимости с оформлением блоков, но
         блок больше не гаснет: он либо на сцене, либо ещё не доехал. */
      node.style.setProperty("--block-presence", "1");
      node.dataset.blockHeld = pin.onStage ? "true" : "false";
      node.dataset.blockPhase = pin.phase;
    }
    return { state: "managed" };
  }

  resize(_viewport: MarketingViewport, layout?: MarketingLayout): void {
    if (layout) this.layout = layout;
  }

  destroy(): void {
    this.route?.removeAttribute("data-blocks-mounted");
    for (const node of this.sections.values()) {
      node.style.removeProperty("--block-pin-y");
      node.style.removeProperty("--block-progress");
      node.style.removeProperty("--block-progress-in");
      node.style.removeProperty("--block-progress-late");
      node.style.removeProperty("--block-presence");
      delete node.dataset.blockHeld;
      delete node.dataset.blockPhase;
    }
  }
}

export { PINNED_SECTIONS };

export const createMarketingBlocks = (root: ParentNode): MarketingBlocks => new MarketingBlocks(root);
